/* eslint-disable react-refresh/only-export-components */
/**
 * Authentication Context
 *
 * Provides global auth state and methods using Firebase Auth SDK.
 *
 * CRITICAL CONSTRAINTS:
 * - NO localStorage/sessionStorage (Firebase Auth handles persistence)
 * - Firebase Auth automatically refreshes tokens (1 hour expiry)
 * - onAuthStateChanged listener provides persistence across page reloads
 * - TypeScript strict mode (NO 'any' types)
 */

import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import {
  User,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as firebaseSignOut,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup,
  onAuthStateChanged,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { auth, db } from '@/firebaseConfig';
import { UserDocument, UserRole } from '@/shared/types/user.types';
import type { AuthContextType } from '@/types/auth.types';
import { getAuthErrorMessage, isAuthError } from '@/utils/authErrors';

// Create AuthContext with undefined default
const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Custom hook to access AuthContext
 * @throws Error if used outside AuthProvider
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

/**
 * AuthProvider Component
 *
 * Wraps app to provide authentication state and methods.
 * Uses Firebase onAuthStateChanged for automatic persistence.
 */
export function AuthProvider({ children }: AuthProviderProps): React.JSX.Element {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const isMocking = useRef(false);

  /**
   * Sign up with email and password
   * Creates Firebase Auth user AND Firestore user document
   */
  const signUp = async (
    email: string,
    password: string,
    displayName: string
  ): Promise<void> => {
    try {
      // Create Firebase Auth user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update display name in Firebase Auth
      await updateProfile(user, { displayName });

      // Check if user document already exists
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        // Create new user document
        const userDoc: UserDocument = {
          uid: user.uid,
          email: user.email || email,
          displayName: displayName,
          photoURL: null,
          role: UserRole.USER,
          credits: 0, // REVERTED: Security handled by backend trigger
          storageUsed: 0,
          storageLimit: 250 * 1024 * 1024, // 250MB in bytes
          createdAt: Timestamp.now(),
          lastLogin: Timestamp.now(),
        };

        await setDoc(userDocRef, userDoc);
      } else {
        // Update existing user document (merge)
        // Only update fields that should change on re-signup (if any)
        await updateDoc(userDocRef, {
          displayName: displayName, // Update display name
          lastLogin: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Sign up error:', error);
      if (isAuthError(error)) {
        throw new Error(getAuthErrorMessage(error.code));
      }
      throw new Error('Failed to create account. Please try again.');
    }
  };

  /**
   * Sign in with email and password
   * Updates lastLogin timestamp in Firestore
   */
  const signIn = async (email: string, password: string): Promise<void> => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      // Update lastLogin timestamp
      await updateDoc(doc(db, 'users', userCredential.user.uid), {
        lastLogin: Timestamp.now(),
      });
    } catch (error) {
      console.error('Sign in error:', error);
      if (isAuthError(error)) {
        throw new Error(getAuthErrorMessage(error.code));
      }
      throw new Error('Failed to sign in. Please try again.');
    }
  };

  /**
   * Sign out current user
   */
  const signOut = async (): Promise<void> => {
    try {
      await firebaseSignOut(auth);
    } catch (error) {
      console.error('Sign out error:', error);
      throw new Error('Failed to sign out. Please try again.');
    }
  };

  /**
   * Send password reset email
   */
  const resetPassword = async (email: string): Promise<void> => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error('Password reset error:', error);
      if (isAuthError(error)) {
        throw new Error(getAuthErrorMessage(error.code));
      }
      throw new Error('Failed to send password reset email. Please try again.');
    }
  };

  /**
   * Sign in with Google
   * Creates Firestore user document if first-time user
   */
  const signInWithGoogle = async (): Promise<void> => {
    try {
      const provider = new GoogleAuthProvider();
      const userCredential = await signInWithPopup(auth, provider);
      const user = userCredential.user;

      // Check if user document exists
      const userDocRef = doc(db, 'users', user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        // First-time Google user - create Firestore document
        const userDoc: UserDocument = {
          uid: user.uid,
          email: user.email || '',
          displayName: user.displayName || 'User',
          photoURL: user.photoURL,
          role: UserRole.USER,
          credits: 0,
          storageUsed: 0,
          storageLimit: 250 * 1024 * 1024, // 250MB
          createdAt: Timestamp.now(),
          lastLogin: Timestamp.now(),
        };

        await setDoc(userDocRef, userDoc);
      } else {
        // Existing user - update lastLogin
        await updateDoc(userDocRef, {
          lastLogin: Timestamp.now(),
        });
      }
    } catch (error) {
      console.error('Google sign in error:', error);
      if (isAuthError(error)) {
        throw new Error(getAuthErrorMessage(error.code));
      }
      throw new Error('Failed to sign in with Google. Please try again.');
    }
  };

  /**
   * Firebase Auth State Listener
   *
   * CRITICAL: This provides automatic persistence across page reloads
   * NO localStorage needed - Firebase Auth SDK handles token storage
   */
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (isMocking.current) return;
      setCurrentUser(user);
      setLoading(false);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, []);

  // Expose auth state for E2E testing
  useEffect(() => {
    if (import.meta.env.DEV) {
      console.log('AuthContext: Mounting E2E hook. DEV mode:', import.meta.env.DEV);

      // Check for injected mock user (from addInitScript)
      // @ts-expect-error - Mocking for E2E
      if (window.__MOCK_USER__) {
        console.log('AuthContext: Found mock user, setting state');
        isMocking.current = true;
        // @ts-expect-error - Mocking for E2E
        setCurrentUser(window.__MOCK_USER__ as User);
        setLoading(false);
      }

      // @ts-expect-error - Mocking for E2E
      window.mockAuthState = (user: Partial<User> | null) => {
        console.log('AuthContext: mockAuthState called', user);
        isMocking.current = true;
        setCurrentUser(user as User);
        setLoading(false);
      };
    }
  }, []);

  const value: AuthContextType = {
    currentUser,
    loading,
    signIn,
    signUp,
    signOut,
    resetPassword,
    signInWithGoogle,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
